import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function CreateSession() {
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [guests, setGuests] = useState([]); // [{ guestName, hostUserId }]
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    API.get('/locations').then(res => setLocations(res.data)).catch(() => {});
    API.get('/admin/users').then(res => {
      setUsers(res.data);
      setSelectedPlayers([user.id]);
    }).catch(() => { setSelectedPlayers([user.id]); });
  }, [user.id]);

  const togglePlayer = (id) => {
    setSelectedPlayers(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const addGuest = () => setGuests([...guests, { guestName: '', hostUserId: user.id }]);
  const removeGuest = (i) => setGuests(guests.filter((_, idx) => idx !== i));
  const updateGuest = (i, field, value) => {
    const updated = [...guests];
    updated[i][field] = value;
    setGuests(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!locationId) return setError('Please select a location');
    if (selectedPlayers.length < 2) return setError('Select at least 2 players');
    const validGuests = guests.filter(g => g.guestName.trim() && g.hostUserId);
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/sessions', {
        locationId, sessionDate,
        playerIds: selectedPlayers,
        guests: validGuests,
      });
      navigate(`/sessions/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating session');
    } finally {
      setLoading(false);
    }
  };

  const totalHeads = selectedPlayers.length + guests.filter(g => g.guestName.trim()).length;

  return (
    <div className="app-shell">
      <div className="topbar">
        <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ padding: '8px 0', color: 'var(--text2)' }}>← Back</button>
        <div className="topbar-title">New Session</div>
        <div style={{ width: 60 }} />
      </div>
      <div className="page fade-in">
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Location</label>
            <select className="form-input" value={locationId} onChange={e => setLocationId(e.target.value)} required>
              <option value="">Select a court...</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={sessionDate} onChange={e => setSessionDate(e.target.value)} required />
          </div>

          <div className="form-group">
            <label className="form-label">Players ({selectedPlayers.length} selected)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
              {users.map(u => (
                <button type="button" key={u.id}
                  className={`chip ${selectedPlayers.includes(u.id) ? 'selected' : ''}`}
                  onClick={() => togglePlayer(u.id)}>
                  {selectedPlayers.includes(u.id) ? '✓ ' : ''}{u.name}{u.id === user.id ? ' (you)' : ''}
                </button>
              ))}
            </div>
          </div>

          {/* Guests */}
          <div className="form-group">
            <div className="row-between" style={{ marginBottom: 10 }}>
              <label className="form-label" style={{ margin: 0 }}>Guests ({guests.length})</label>
              <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--accent)', padding: '4px 8px' }} onClick={addGuest}>
                + Add Guest
              </button>
            </div>
            {guests.map((g, i) => (
              <div key={i} className="card" style={{ padding: '12px', marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    className="form-input"
                    placeholder="Guest name"
                    value={g.guestName}
                    onChange={e => updateGuest(i, 'guestName', e.target.value)}
                  />
                  <button type="button" className="btn btn-ghost" style={{ color: 'var(--danger)', padding: '8px', flexShrink: 0 }} onClick={() => removeGuest(i)}>✕</button>
                </div>
                <div>
                  <label className="form-label">Host (pays for this guest)</label>
                  <select className="form-input" value={g.hostUserId} onChange={e => updateGuest(i, 'hostUserId', e.target.value)}>
                    {users.filter(u => selectedPlayers.includes(u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.name}{u.id === user.id ? ' (you)' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>

          {/* Cost summary */}
          {totalHeads > 0 && (
            <div className="card" style={{ background: 'var(--accent-light)', border: '1px solid #bfdbfe', padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>
                💡 {totalHeads} total heads ({selectedPlayers.length} players{guests.length > 0 ? ` + ${guests.filter(g => g.guestName.trim()).length} guest${guests.filter(g => g.guestName.trim()).length > 1 ? 's' : ''}` : ''})
              </div>
              {guests.filter(g => g.guestName.trim()).length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                  Guest hosts pay double share
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Session 🎾'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
